// src/Components/ProjectBundleAuthoringPanel/ProjectBundleAuthoringPanel.tsx
//
// DEV-1038. Permission Sets (bundle authoring) section on the Project Access
// tab. Replaces the bundle-CRUD half of ProjectPermissionsV2Panel (the other
// half — role-slot grants — already retired in DEV-1042/1043 on the Teams tab).
//
// Surface contents:
//   * List of platform bundles + project-scoped bundles visible to this project
//   * [+ New Permission Set] opens the tray in create mode
//   * [view] / [edit] / [clone] / [delete] per row depending on bundle origin
//   * Tray supports five modes: create, edit, clone, view, delete-confirm
//
// Backend wiring:
//   * listPermissionSetsForScope("project", projectGuid) + capabilities()
//   * createBundleForScope (new bundle)
//   * updateBundleForScope (edit existing project-scoped bundle; DEV-1038 added)
//   * deletePermissionSet
//
// System bundles (is_system=TRUE) are READ-ONLY — view + clone only, no edit
// or delete. Project-scoped bundles get the full action set.
//
// UX pattern locked in DEV-1038 mockup session (Skip 2026-05-15):
//   * Replace pill explosion with [view] button on rows (cards stay clean)
//   * Capability checklist grouped by domain prefix (impact / about / risks / ...)
//   * Clone & Customize as the override path for system bundles
//   * Auditor + Observer as new platform bundles (migration 157)

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import type {
  CapabilityDto,
  CapabilityName,
  PermissionSetDto,
} from "../../security/v2/types";
import { PLATFORM_PERMISSION_SET_GUIDS } from "../../security/v2/types";
import { normalizeGuid } from "../../utils/guid";
import { Tray } from "../Tray/Tray";
import styles from "./ProjectBundleAuthoringPanel.module.css";

interface Props {
  projectGuid: string;
  /** Project name surfaced in the section header if provided. */
  projectName?: string;
  /** Parent gates admin-only mutations. Default true. */
  canAdmin?: boolean;
}

interface LoadState {
  loading: boolean;
  error: string | null;
  bundles: PermissionSetDto[];
  capabilities: CapabilityDto[];
}

const initialState: LoadState = {
  loading: true,
  error: null,
  bundles: [],
  capabilities: [],
};

type TrayMode = "create" | "edit" | "clone" | "view";

interface TrayCtx {
  mode: TrayMode;
  source?: PermissionSetDto;
}

export default function ProjectBundleAuthoringPanel({
  projectGuid,
  projectName,
  canAdmin = true,
}: Props) {
  const { t } = useAppTranslation("projectConfig");
  const [state, setState] = useState<LoadState>(initialState);
  const [busy, setBusy] = useState(false);

  // Tray + form state
  const [trayCtx, setTrayCtx] = useState<TrayCtx | null>(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formGrantMode, setFormGrantMode] = useState<"user" | "slot" | "">("");
  const [formCaps, setFormCaps] = useState<Set<CapabilityName>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // ── Data load ────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!projectGuid) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [bundles, capabilities] = await Promise.all([
        permissionsApiV2.listPermissionSetsForScope("project", projectGuid),
        permissionsApiV2.capabilities(),
      ]);
      setState({ loading: false, error: null, bundles, capabilities });
    } catch (err: any) {
      setState((s) => ({
        ...s,
        loading: false,
        error: String(err?.message ?? err),
      }));
    }
  }, [projectGuid]);

  useEffect(() => { load(); }, [load]);

  // ── Derived: project-scope capabilities grouped by domain ────

  const groupedCaps = useMemo(() => {
    // Only project-scope caps that are assignable can appear in project bundles.
    const eligible = state.capabilities.filter(
      (c) => c.isAssignable && c.scopeType === "project"
    );
    const groups = new Map<string, CapabilityDto[]>();
    for (const c of eligible) {
      const domain = c.name.includes(":") ? c.name.split(":")[0] : "other";
      const arr = groups.get(domain) ?? [];
      arr.push(c);
      groups.set(domain, arr);
    }
    // Sort caps within each group alphabetically.
    for (const [k, arr] of groups) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
      groups.set(k, arr);
    }
    // Sort groups by domain name.
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a.localeCompare(b)
    );
  }, [state.capabilities]);

  const platformGuids = useMemo(() => {
    const s = new Set<string>();
    for (const b of state.bundles) {
      if (b.ownershipScope === "platform") s.add(b.permissionSetGuid.toUpperCase());
    }
    return s;
  }, [state.bundles]);

  // ── Tray lifecycle ───────────────────────────────────────────

  function resetForm() {
    setFormName("");
    setFormDesc("");
    setFormGrantMode("");
    setFormCaps(new Set());
    setConfirmingDelete(false);
  }

  function loadBundleIntoForm(b: PermissionSetDto, nameSuffix = "") {
    setFormName(b.name + nameSuffix);
    setFormDesc(b.description ?? "");
    const mode = b.grantMode ?? "user";
    setFormGrantMode(mode === "slot" ? "slot" : "user");
    setFormCaps(new Set(b.capabilities ?? []));
    setConfirmingDelete(false);
  }

  const openCreateTray = useCallback(() => {
    resetForm();
    setTrayCtx({ mode: "create" });
  }, []);

  const openEditTray = useCallback((b: PermissionSetDto) => {
    loadBundleIntoForm(b);
    setTrayCtx({ mode: "edit", source: b });
  }, []);

  const openViewTray = useCallback((b: PermissionSetDto) => {
    loadBundleIntoForm(b);
    setTrayCtx({ mode: "view", source: b });
  }, []);

  const openCloneTray = useCallback((b: PermissionSetDto) => {
    // Pre-fill from source but rename for clarity ("Copy of …" suffix).
    // User can edit before saving. Clone is always a CREATE under the hood.
    loadBundleIntoForm(b, " (copy)");
    setTrayCtx({ mode: "clone", source: b });
  }, []);

  const closeTray = useCallback(() => {
    setTrayCtx(null);
    resetForm();
  }, []);

  // ── Mutations ────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!canAdmin || !trayCtx) return;
    if (!formName.trim()) return;
    if (formCaps.size === 0) return;
    if (!formGrantMode) return;
    setBusy(true);
    try {
      if (trayCtx.mode === "edit" && trayCtx.source) {
        await permissionsApiV2.updateBundleForScope(
          "project",
          projectGuid,
          trayCtx.source.permissionSetGuid,
          {
            name: formName.trim(),
            description: formDesc.trim(),
            capabilities: Array.from(formCaps),
            grantMode: formGrantMode,
          }
        );
      } else {
        // create OR clone — both write a new bundle
        await permissionsApiV2.createBundleForScope("project", projectGuid, {
          name: formName.trim(),
          description: formDesc.trim(),
          capabilities: Array.from(formCaps),
          grantMode: formGrantMode,
        });
      }
      closeTray();
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  }, [canAdmin, trayCtx, formName, formDesc, formCaps, formGrantMode, projectGuid, load, closeTray]);

  const handleDelete = useCallback(async () => {
    if (!canAdmin || !trayCtx?.source) return;
    setBusy(true);
    try {
      await permissionsApiV2.deletePermissionSet(trayCtx.source.permissionSetGuid);
      closeTray();
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  }, [canAdmin, trayCtx, load, closeTray]);

  // ── Form helpers ─────────────────────────────────────────────

  function toggleCap(name: CapabilityName) {
    setFormCaps((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleGroup(domain: string, caps: CapabilityDto[]) {
    setFormCaps((prev) => {
      const next = new Set(prev);
      const allSelected = caps.every((c) => next.has(c.name));
      if (allSelected) {
        for (const c of caps) next.delete(c.name);
      } else {
        for (const c of caps) next.add(c.name);
      }
      return next;
    });
  }

  // ── Render helpers ───────────────────────────────────────────

  function renderRow(b: PermissionSetDto) {
    const isSystem = b.isSystem;
    const isPlatform = b.ownershipScope === "platform";
    // DEV-1039 iter 3. Block cloning Owner. A clone of Owner would have
    // Owner-level capabilities (including project deletion) and could be
    // granted to multiple people — bypassing the singleton invariant we
    // enforce by setting project.ownerGuid in Basics. Admins who need
    // Owner-like permissions for a second person should use Admin.
    const isOwnerBundle =
      normalizeGuid(b.permissionSetGuid) ===
      PLATFORM_PERMISSION_SET_GUIDS.Owner.toUpperCase();
    const scopePill = isPlatform ? (
      <>
        <span className={styles.pillPlatform}>{t("access.bundles.scopePlatform")}</span>
        {isSystem && <span className={styles.pillSystem}>{t("access.bundles.scopeSystem")}</span>}
      </>
    ) : (
      <span className={styles.pillProject}>{t("access.bundles.scopeProject")}</span>
    );
    const modePill =
      b.grantMode === "slot" ? (
        <span className={styles.pillOperational}>{t("access.bundles.modeOperational")}</span>
      ) : (
        <span className={styles.pillGovernance}>{t("access.bundles.modeGovernance")}</span>
      );
    const twoFa = b.requires2fa ? (
      <span className={styles.pill2fa} title={t("access.bundles.twoFaTooltip")}>
        {t("access.bundles.twoFa")}
      </span>
    ) : null;

    return (
      <div key={b.permissionSetGuid} className={styles.row}>
        <div className={styles.rowMain}>
          <div className={styles.rowTitle}>
            {b.name}
            {scopePill}
            {modePill}
            {twoFa}
          </div>
          {b.description && (
            <div className={styles.rowSubtitle}>{b.description}</div>
          )}
        </div>
        <div className={styles.rowActions}>
          {isSystem ? (
            // System: view + clone. No edit, no delete.
            // DEV-1039 iter 3: Owner is view-only (no clone) — see comment
            // on isOwnerBundle above.
            <>
              <button
                type="button"
                className={styles.btnRow}
                onClick={() => openViewTray(b)}
              >
                {t("access.bundles.view")}
              </button>
              {canAdmin && !isOwnerBundle && (
                <button
                  type="button"
                  className={styles.btnRow}
                  onClick={() => openCloneTray(b)}
                  title={t("access.bundles.cloneTooltip")}
                >
                  {t("access.bundles.clone")}
                </button>
              )}
            </>
          ) : (
            // Project-scoped: edit + clone + (delete via edit tray).
            <>
              <button
                type="button"
                className={styles.btnRow}
                onClick={() => openEditTray(b)}
              >
                {canAdmin
                  ? t("access.bundles.edit")
                  : t("access.bundles.view")}
              </button>
              {canAdmin && (
                <button
                  type="button"
                  className={styles.btnRow}
                  onClick={() => openCloneTray(b)}
                  title={t("access.bundles.cloneTooltip")}
                >
                  {t("access.bundles.clone")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Tray render ──────────────────────────────────────────────

  function renderTrayBody() {
    const readonly = trayCtx?.mode === "view";
    const sourceName = trayCtx?.source?.name ?? "";
    const sourceIsSystem = trayCtx?.source?.isSystem ?? false;

    return (
      <>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{t("access.bundles.fieldName")}</label>
          <input
            type="text"
            className={styles.input}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            disabled={readonly || busy}
            placeholder={t("access.bundles.namePlaceholder")}
            maxLength={80}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {t("access.bundles.fieldDescription")}
            <span className={styles.fieldSub}>{t("access.bundles.fieldDescriptionSub")}</span>
          </label>
          <textarea
            className={styles.textarea}
            value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
            disabled={readonly || busy}
            placeholder={t("access.bundles.descPlaceholder")}
            maxLength={400}
            rows={2}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{t("access.bundles.fieldGrantMode")}</label>
          <select
            className={styles.input}
            value={formGrantMode}
            onChange={(e) => setFormGrantMode(e.target.value as "user" | "slot" | "")}
            disabled={readonly || busy}
          >
            <option value="">{t("access.bundles.chooseGrantMode")}</option>
            <option value="user">{t("access.bundles.modeGovernanceLong")}</option>
            <option value="slot">{t("access.bundles.modeOperationalLong")}</option>
          </select>
          {formGrantMode && (
            <div className={styles.fieldHint}>
              {formGrantMode === "user"
                ? t("access.bundles.modeGovernanceHint")
                : t("access.bundles.modeOperationalHint")}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {t("access.bundles.fieldCapabilities")}
            <span className={styles.fieldSub}>
              {t("access.bundles.capsSelectedCount", { count: formCaps.size })}
            </span>
          </label>
          {!formGrantMode ? (
            <div className={styles.capsEmpty}>
              {t("access.bundles.capsPickModeFirst")}
            </div>
          ) : (
            <div className={styles.capsList}>
              {groupedCaps.map(([domain, caps]) => {
                const allSelected = caps.every((c) => formCaps.has(c.name));
                const someSelected = caps.some((c) => formCaps.has(c.name));
                return (
                  <div key={domain} className={styles.capGroup}>
                    <div className={styles.capGroupHeader}>
                      <span className={styles.capGroupTitle}>{domain}</span>
                      {!readonly && (
                        <button
                          type="button"
                          className={styles.capGroupToggle}
                          onClick={() => toggleGroup(domain, caps)}
                          disabled={busy}
                        >
                          {allSelected
                            ? t("access.bundles.deselectAll")
                            : someSelected
                              ? t("access.bundles.selectAll")
                              : t("access.bundles.selectAll")}
                        </button>
                      )}
                    </div>
                    <div className={styles.capGroupBody}>
                      {caps.map((c) => (
                        <label key={c.name} className={styles.capOption}>
                          <input
                            type="checkbox"
                            checked={formCaps.has(c.name)}
                            onChange={() => toggleCap(c.name)}
                            disabled={readonly || busy}
                          />
                          <div className={styles.capOptionInfo}>
                            <div className={styles.capOptionName}>{c.name}</div>
                            {c.description && (
                              <div className={styles.capOptionDesc}>
                                {c.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Edit-mode inline delete confirmation. */}
        {trayCtx?.mode === "edit" && confirmingDelete && trayCtx.source && (
          <div className={styles.deleteConfirm}>
            <div className={styles.deleteConfirmText}>
              {t("access.bundles.deleteConfirmText", { name: sourceName })}
            </div>
            <div className={styles.deleteConfirmActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
              >
                {t("access.bundles.cancel")}
              </button>
              <button
                type="button"
                className={styles.btnDangerSolid}
                onClick={handleDelete}
                disabled={busy}
              >
                {t("access.bundles.confirmDelete")}
              </button>
            </div>
          </div>
        )}

        {/* System bundles in view mode get a callout pointing to Clone. */}
        {readonly && sourceIsSystem && canAdmin && (
          <div className={styles.viewCallout}>
            {t("access.bundles.viewSystemCallout")}
          </div>
        )}
      </>
    );
  }

  function renderTrayFooter() {
    if (!trayCtx) return null;
    const readonly = trayCtx.mode === "view";
    const canSubmit =
      !!formName.trim() && formCaps.size > 0 && !!formGrantMode;
    const submitLabel =
      trayCtx.mode === "edit"
        ? t("access.bundles.submitSave")
        : trayCtx.mode === "clone"
          ? t("access.bundles.submitClone")
          : t("access.bundles.submitCreate");

    return (
      <>
        {/* Delete on the left for edit mode (project-scoped only). */}
        {trayCtx.mode === "edit" &&
        !confirmingDelete &&
        trayCtx.source &&
        !trayCtx.source.isSystem ? (
          <button
            type="button"
            className={styles.btnDanger}
            onClick={() => setConfirmingDelete(true)}
            disabled={busy}
          >
            {t("access.bundles.delete")}
          </button>
        ) : (
          <span />
        )}

        <div className={styles.trayFooterEnd}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={closeTray}
            disabled={busy}
          >
            {readonly ? t("access.bundles.close") : t("access.bundles.cancel")}
          </button>
          {!readonly && (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={!canSubmit || busy}
            >
              {submitLabel}
            </button>
          )}
        </div>
      </>
    );
  }

  // ── Render ───────────────────────────────────────────────────

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {t("access.bundles.title")}
            {state.bundles.length > 0 && (
              <span className={styles.count}>({state.bundles.length})</span>
            )}
          </div>
          {canAdmin && !state.loading && (
            <button
              type="button"
              className={styles.btnAdd}
              onClick={openCreateTray}
            >
              + {t("access.bundles.newBundle")}
            </button>
          )}
        </div>

        <div className={styles.sectionBody}>
          {state.error && (
            <div className={styles.errorBanner}>
              <span>{t("access.bundles.errorBanner")}</span>
              <button
                type="button"
                onClick={load}
                className={styles.btnRetry}
              >
                {t("access.bundles.retry")}
              </button>
            </div>
          )}

          {state.loading ? (
            <div className={styles.list}>
              <div className={styles.skeletonRow} />
              <div className={styles.skeletonRow} />
              <div className={styles.skeletonRow} />
            </div>
          ) : state.bundles.length === 0 && !state.error ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{t("access.bundles.emptyTitle")}</div>
              <div className={styles.emptyHelp}>{t("access.bundles.emptyHelp")}</div>
            </div>
          ) : (
            <div className={styles.list}>
              {state.bundles.map(renderRow)}
            </div>
          )}
        </div>
      </div>

      <Tray
        open={trayCtx !== null}
        onClose={closeTray}
        title={
          trayCtx?.mode === "create"
            ? t("access.bundles.trayCreateTitle")
            : trayCtx?.mode === "clone"
              ? t("access.bundles.trayCloneTitle", {
                  name: trayCtx.source?.name ?? "",
                })
              : trayCtx?.mode === "view"
                ? t("access.bundles.trayViewTitle", {
                    name: trayCtx.source?.name ?? "",
                  })
                : trayCtx?.mode === "edit"
                  ? t("access.bundles.trayEditTitle", {
                      name: trayCtx.source?.name ?? "",
                    })
                  : undefined
        }
        width={540}
      >
        <Tray.Body className={styles.trayBody}>{renderTrayBody()}</Tray.Body>
        <Tray.Footer className={styles.trayFooter}>{renderTrayFooter()}</Tray.Footer>
      </Tray>
    </>
  );
}
